/**
 * ExpedientePublico.js - Cartilla de vacunacion publica
 *
 * Accesible via: /expediente/:token
 * No requiere autenticacion
 * Muestra vacunas, desparasitaciones y lugares de confianza
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../services/api/config';

export default function ExpedientePublico() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    async function cargarExpediente() {
      try {
        const resp = await fetch(`${API_URL}/api/expediente-publico/${token}`);
        const json = await resp.json();

        if (!resp.ok) {
          setError(json.error || 'Cartilla no encontrada');
          return;
        }

        setData(json);
      } catch (err) {
        console.error('Error cargando cartilla:', err);
        setError('Error de conexion');
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      cargarExpediente();
    }
  }, [token]);

  // Calcular edad aproximada
  function calcularEdad(fechaNacimiento) {
    if (!fechaNacimiento) return null;
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    const diff = hoy - nacimiento;
    const years = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
    const months = Math.floor((diff % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));

    if (years > 0) {
      return `${years} ${years === 1 ? 'ano' : 'anos'}${months > 0 ? ` y ${months} ${months === 1 ? 'mes' : 'meses'}` : ''}`;
    }
    return `${months} ${months === 1 ? 'mes' : 'meses'}`;
  }

  // Formatear fecha
  function formatFecha(dateStr) {
    if (!dateStr) return 'Sin fecha';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Estado de vacuna basado en proximaDosis
  function getVacunaEstado(vacuna) {
    const proxima = vacuna.proximaDosis || vacuna.fechaProximaDosis;
    if (!proxima) {
      return { label: 'Aplicada', color: '#4CAF50', bg: '#e8f5e9', icon: 'bi-check-circle-fill' };
    }
    const hoy = new Date();
    const fechaProxima = new Date(proxima);
    const diffDias = Math.floor((fechaProxima - hoy) / (1000 * 60 * 60 * 24));

    if (diffDias < 0) {
      return { label: 'Vencida', color: '#dc3545', bg: '#fdecea', icon: 'bi-exclamation-circle-fill' };
    }
    if (diffDias <= 30) {
      return { label: `Proxima: ${formatFecha(proxima)}`, color: '#f59e0b', bg: '#fffbeb', icon: 'bi-clock-fill' };
    }
    return { label: 'Al dia', color: '#4CAF50', bg: '#e8f5e9', icon: 'bi-check-circle-fill' };
  }

  // Estado de desparasitacion basado en fecha (vence a los 90 dias)
  function getDesparasitacionEstado(desp) {
    const fecha = desp.fecha || desp.fechaAplicacion;
    if (!fecha) return { label: 'Sin fecha', color: '#6b7280', bg: '#f3f4f6' };

    const hoy = new Date();
    const fechaAplicacion = new Date(fecha);
    const diffDias = Math.floor((hoy - fechaAplicacion) / (1000 * 60 * 60 * 24));

    if (diffDias > 90) {
      return { label: 'Vencida', color: '#dc3545', bg: '#fdecea' };
    }
    return { label: 'Al dia', color: '#4CAF50', bg: '#e8f5e9' };
  }

  // Icono por categoria de lugar
  function getIconoCategoria(categoria) {
    const iconos = {
      'veterinaria': '🏥',
      'farmacia': '💊',
      'pension': '🏠',
      'estilistas': '✂️',
      'parques': '🌳',
      'tiendas': '🏪',
      'cerrajeria': '🔑',
      'mecanico': '🔧',
      'conveniencia': '🏪'
    };
    return iconos[(categoria || '').toLowerCase()] || '📍';
  }

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}
      >
        <div className="text-center">
          <div className="spinner-border mb-3" style={{ color: '#4CAF50' }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p style={{ color: '#6b7280' }}>Cargando cartilla...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}
      >
        <div
          style={{
            maxWidth: '500px',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          <div className="text-center" style={{ padding: '40px' }}>
            <i
              className="bi bi-exclamation-triangle"
              style={{ fontSize: '4rem', color: '#f59e0b' }}
            ></i>
            <h4 className="mt-3" style={{ color: '#1f2937' }}>Cartilla no disponible</h4>
            <p style={{ color: '#6b7280' }}>{error}</p>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              El enlace puede haber expirado o ser invalido.
              Contacta al dueno de la mascota para obtener un nuevo enlace.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const mascota = data.mascota || {};
  const vacunas = data.vacunas || [];
  const desparasitaciones = data.desparasitaciones || [];
  const lugaresConfianza = mascota.expedienteLugaresConfianza || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '20px 16px' }}>
      <div className="container" style={{ maxWidth: '700px' }}>

        {/* Header */}
        <div
          style={{
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '24px'
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #2e7d32 100%)',
              padding: '24px',
              color: 'white'
            }}
          >
            <div className="d-flex align-items-center">
              {mascota.fotoUrl && (
                <img
                  src={mascota.fotoUrl}
                  alt={mascota.nombreMascota}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid rgba(255,255,255,0.8)',
                    marginRight: '16px',
                    flexShrink: 0
                  }}
                />
              )}
              <div>
                <h2 style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '1.5rem' }}>
                  Cartilla de {mascota.nombreMascota || 'Mascota'}
                </h2>
                <p style={{ marginBottom: 0, opacity: 0.85, fontSize: '0.9rem' }}>
                  {mascota.especie && <span className="me-3">{mascota.especie}</span>}
                  {mascota.raza && <span className="me-3">{mascota.raza}</span>}
                  {mascota.fechaNacimiento && (
                    <span>Edad: {calcularEdad(mascota.fechaNacimiento)}</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Info de la mascota */}
          <div style={{ backgroundColor: '#fff', padding: '16px 24px' }}>
            <div className="row">
              {mascota.sexo && (
                <div className="col-6 col-md-3 mb-2">
                  <small style={{ color: '#6b7280', display: 'block' }}>Sexo</small>
                  <strong style={{ color: '#1f2937' }}>{mascota.sexo}</strong>
                </div>
              )}
              {mascota.color && (
                <div className="col-6 col-md-3 mb-2">
                  <small style={{ color: '#6b7280', display: 'block' }}>Color</small>
                  <strong style={{ color: '#1f2937' }}>{mascota.color}</strong>
                </div>
              )}
              {mascota.fechaNacimiento && (
                <div className="col-6 col-md-3 mb-2">
                  <small style={{ color: '#6b7280', display: 'block' }}>Nacimiento</small>
                  <strong style={{ color: '#1f2937' }}>{new Date(mascota.fechaNacimiento).toLocaleDateString('es-MX')}</strong>
                </div>
              )}
            </div>

            {/* Alertas medicas */}
            {(mascota.alergias || mascota.condicionesMedicas) && (
              <div style={{ marginTop: '12px' }}>
                {mascota.alergias && (
                  <div
                    style={{
                      backgroundColor: '#fef3c7',
                      border: '1px solid #fcd34d',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      marginBottom: '8px',
                      fontSize: '0.875rem',
                      color: '#92400e'
                    }}
                  >
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Alergias:</strong> {mascota.alergias}
                  </div>
                )}
                {mascota.condicionesMedicas && (
                  <div
                    style={{
                      backgroundColor: '#dbeafe',
                      border: '1px solid #93c5fd',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '0.875rem',
                      color: '#1e40af'
                    }}
                  >
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Condiciones:</strong> {mascota.condicionesMedicas}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Seccion Vacunas */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '24px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '16px 24px',
              borderBottom: '1px solid #f3f4f6',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>💉</span>
            <h5 style={{ margin: 0, fontWeight: 600, color: '#1f2937' }}>
              Vacunas
            </h5>
            {vacunas.length > 0 && (
              <span
                style={{
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  borderRadius: '12px',
                  padding: '2px 10px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  marginLeft: 'auto'
                }}
              >
                {vacunas.length}
              </span>
            )}
          </div>
          <div style={{ padding: '16px 24px' }}>
            {vacunas.length === 0 ? (
              <div className="text-center" style={{ padding: '32px 0', color: '#9ca3af' }}>
                <i className="bi bi-inbox" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '8px' }}></i>
                <p style={{ margin: 0 }}>No hay vacunas registradas</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {vacunas
                  .sort((a, b) => new Date(b.fecha || b.fechaAplicacion || 0) - new Date(a.fecha || a.fechaAplicacion || 0))
                  .map((vacuna, idx) => {
                    const estado = getVacunaEstado(vacuna);
                    return (
                      <div
                        key={vacuna.id || idx}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          backgroundColor: '#fafafa'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong style={{ color: '#1f2937', fontSize: '0.95rem' }}>
                              {vacuna.nombre || vacuna.vacuna || 'Vacuna'}
                            </strong>
                            <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '4px' }}>
                              {formatFecha(vacuna.fecha || vacuna.fechaAplicacion)}
                              {vacuna.lote && <span> &middot; Lote: {vacuna.lote}</span>}
                            </div>
                            {(vacuna.proximaDosis || vacuna.fechaProximaDosis) && (
                              <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '2px' }}>
                                Proxima dosis: {formatFecha(vacuna.proximaDosis || vacuna.fechaProximaDosis)}
                              </div>
                            )}
                          </div>
                          <span
                            style={{
                              backgroundColor: estado.bg,
                              color: estado.color,
                              borderRadius: '8px',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}
                          >
                            <i className={`bi ${estado.icon} me-1`}></i>
                            {estado.label}
                          </span>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            )}
          </div>
        </div>

        {/* Seccion Desparasitaciones */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '24px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '16px 24px',
              borderBottom: '1px solid #f3f4f6',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span style={{ fontSize: '1.25rem' }}>🛡️</span>
            <h5 style={{ margin: 0, fontWeight: 600, color: '#1f2937' }}>
              Desparasitaciones
            </h5>
            {desparasitaciones.length > 0 && (
              <span
                style={{
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  borderRadius: '12px',
                  padding: '2px 10px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  marginLeft: 'auto'
                }}
              >
                {desparasitaciones.length}
              </span>
            )}
          </div>
          <div style={{ padding: '16px 24px' }}>
            {desparasitaciones.length === 0 ? (
              <div className="text-center" style={{ padding: '32px 0', color: '#9ca3af' }}>
                <i className="bi bi-inbox" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '8px' }}></i>
                <p style={{ margin: 0 }}>No hay desparasitaciones registradas</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {desparasitaciones
                  .sort((a, b) => new Date(b.fecha || b.fechaAplicacion || 0) - new Date(a.fecha || a.fechaAplicacion || 0))
                  .map((desp, idx) => {
                    const estado = getDesparasitacionEstado(desp);
                    return (
                      <div
                        key={desp.id || idx}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          backgroundColor: '#fafafa'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong style={{ color: '#1f2937', fontSize: '0.95rem' }}>
                              {desp.tipo || 'Desparasitacion'}
                              {desp.subtipo && ` - ${desp.subtipo}`}
                            </strong>
                            {(desp.producto || desp.nombre) && (
                              <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '4px' }}>
                                {desp.producto || desp.nombre}
                              </div>
                            )}
                            <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '2px' }}>
                              {formatFecha(desp.fecha || desp.fechaAplicacion)}
                            </div>
                          </div>
                          <span
                            style={{
                              backgroundColor: estado.bg,
                              color: estado.color,
                              borderRadius: '8px',
                              padding: '4px 10px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}
                          >
                            {estado.label}
                          </span>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            )}
          </div>
        </div>

        {/* Seccion Lugares de Confianza */}
        {lugaresConfianza.length > 0 && (
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '24px',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid #f3f4f6',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '1.25rem' }}>📍</span>
              <h5 style={{ margin: 0, fontWeight: 600, color: '#1f2937' }}>
                Lugares de Confianza
              </h5>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <div className="d-flex flex-column gap-3">
                {lugaresConfianza.map((lugar, idx) => (
                  <div
                    key={idx}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      backgroundColor: '#fafafa',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {getIconoCategoria(lugar.categoria)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ color: '#1f2937', fontSize: '0.95rem' }}>
                        {lugar.nombre || 'Sin nombre'}
                      </strong>
                      {lugar.categoria && (
                        <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '2px', textTransform: 'capitalize' }}>
                          {lugar.categoria}
                        </div>
                      )}
                      {lugar.telefono && (
                        <div style={{ color: '#4CAF50', fontSize: '0.8rem', marginTop: '2px' }}>
                          <i className="bi bi-telephone me-1"></i>
                          <a href={`tel:${lugar.telefono}`} style={{ color: '#4CAF50', textDecoration: 'none' }}>
                            {lugar.telefono}
                          </a>
                        </div>
                      )}
                      {lugar.direccion && (
                        <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lugar.direccion}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center" style={{ marginTop: '24px', paddingBottom: '24px' }}>
          <p style={{ color: '#9ca3af', fontSize: '0.8rem', marginBottom: '4px' }}>
            <i className="bi bi-shield-check me-1"></i>
            Cartilla compartida de forma segura via{' '}
            <a
              href="https://lemmontags.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4CAF50', textDecoration: 'none' }}
            >
              LemmonTags
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
